import React, { useEffect, useReducer, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import styles from "./AiboxForm.module.css"
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import PasswordInput from '../../../../Inputs/PasswordInput';
import Lottie from 'lottie-react';
import fail from '../../../../../../assets/animations/fail.json'
import success from '../../../../../../assets/animations/success.json'
import { aiboxLogin, removeAibox, addAibox } from '../../../../../../Api Methods/Api';
import SelectableListRow from '../SelectableListRow';

const TextReducer = (state, action) => {
    if (action.type === 'USER_INPUT') {
        return { value: action.val, isValid: action.val.length > 0 };
    }

    return state;
}

const CONTROL_LEVELS = [
    { value: 'hub', label: 'Hub' },
    { value: 'channel', label: 'Channels' },
    { value: 'rule', label: 'Rules' },
    { value: 'output_relay', label: 'Relays' },
]

// extraField2 holds the zoneConfig array (a single { zoneName, ruleUUIDs, controlLevel } entry)
// that was sent when the integration was created/last updated.
const getExistingZoneConfig = (integration) => {
    try {
        const parsed = integration?.extraField2 ? JSON.parse(integration.extraField2) : [];
        const zoneConfig = Array.isArray(parsed) ? parsed[0] : parsed;
        return {
            zoneName: zoneConfig?.zoneName ?? '',
            controlLevel: zoneConfig?.controlLevel ?? 'hub',
            ruleUUIDs: zoneConfig?.ruleUUIDs ?? []
        };
    } catch {
        return { zoneName: '', controlLevel: 'hub', ruleUUIDs: [] };
    }
}

const AiboxEditForm = ({ integration, apiData, siteUUID, pin, onClose, refreshDevices }) => {
    const dispatch = useDispatch();
    const userID = useSelector(state => state.auth.userID);

    const [existingIP, existingPort] = (integration?.extraField1 ?? '').split(':');
    const existingZoneConfig = getExistingZoneConfig(integration);

    const [step, setStep] = useState('form');
    const [failReason, setFailReason] = useState("");

    const [controlLevel, setControlLevel] = useState(existingZoneConfig.controlLevel);
    const [zoneName, setZoneName] = useState(existingZoneConfig.zoneName);

    const [channels, setChannels] = useState([]);
    const [rules, setRules] = useState([]);
    const [relays, setRelays] = useState([]);

    const [selectedChannels, setSelectedChannels] = useState([]);
    const [selectedRules, setSelectedRules] = useState([]);
    const [selectedRelays, setSelectedRelays] = useState([]);

    const [optionsLoading, setOptionsLoading] = useState(true);
    const [optionsError, setOptionsError] = useState("");

    const handleSelectChannel = (channel) => {
        setSelectedChannels((prevSelectedChannels) => {
            if (prevSelectedChannels.includes(channel)) {
                return prevSelectedChannels.filter((x) => x.name !== channel.name);
            } else {
                return [...prevSelectedChannels, channel];
            }
        });
    }

    const handleSelectRule = (rule) => {
        setSelectedRules((prevSelectedRules) => {
            if (prevSelectedRules.includes(rule)) {
                return prevSelectedRules.filter((x) => x.name !== rule.name);
            } else {
                return [...prevSelectedRules, rule];
            }
        });
    }

    const handleSelectRelay = (relay) => {
        setSelectedRelays((prevSelectedRelays) => {
            if (prevSelectedRelays.includes(relay)) {
                return prevSelectedRelays.filter((x) => x.name !== relay.name);
            } else {
                return [...prevSelectedRelays, relay];
            }
        });
    }

    useEffect(() => {
        const fetchAvailableOptions = async () => {
            setOptionsLoading(true);
            setOptionsError("");

            const loginBody = {
                protocol: "https",
                hostName: existingIP,
                port: existingPort,
                username: integration.username,
                password: integration.password,
                userID
            };

            try {
                const loginResponse = await aiboxLogin(apiData, loginBody, dispatch);
                if (loginResponse.success) {
                    const fetchedChannels = loginResponse.data.channels;
                    const fetchedRules = loginResponse.data.systemRules;
                    const fetchedRelays = loginResponse.data.relayOutputs;

                    setChannels(fetchedChannels);
                    setRules(fetchedRules);
                    setRelays(fetchedRelays);

                    const controlledIds = new Set(existingZoneConfig.ruleUUIDs);
                    setSelectedChannels(fetchedChannels.filter(channel => controlledIds.has(String(channel.no))));
                    setSelectedRules(fetchedRules.filter(rule => controlledIds.has(String(rule.uuid))));
                    setSelectedRelays(fetchedRelays.filter(relay => controlledIds.has(String(relay.relay_id))));
                } else {
                    setOptionsError("Failed to log in to Ganz AiBox to retrieve controlled channels/rules/relays");
                }
            } catch {
                setOptionsError("Failed to log in to Ganz AiBox to retrieve controlled channels/rules/relays");
            } finally {
                setOptionsLoading(false);
            }
        };

        fetchAvailableOptions();
    }, []);

    const [ipAddressState, dispatchIPAddressState] = useReducer(TextReducer, {
        value: existingIP ?? '',
        isValid: true
    });

    const [portState, dispatchPortState] = useReducer(TextReducer, {
        value: existingPort ?? '',
        isValid: true
    });

    const [usernameState, dispatchUsernameState] = useReducer(TextReducer, {
        value: integration?.username ?? '',
        isValid: null
    });

    const [passwordState, dispatchPasswordState] = useReducer(TextReducer, {
        value: integration?.password ?? '',
        isValid: null
    });

    const submitHandler = async (event) => {
        event.preventDefault();
        setStep('saving');

        // Re-authenticate with the (possibly edited) credentials before touching the existing
        // link, so a bad new IP/username/password doesn't leave the pin unlinked.
        const loginBody = {
            protocol: "https",
            hostName: ipAddressState.value,
            port: portState.value,
            username: usernameState.value,
            password: passwordState.value,
            userID
        };

        const loginResponse = await aiboxLogin(apiData, loginBody, dispatch);
        if (!loginResponse.success) {
            setFailReason("Failed to log in to Ganz AiBox with the details provided, please check them and try again");
            setStep('fail');
            return;
        }

        const unlinkResponse = await removeAibox(apiData, siteUUID, pin, dispatch);
        if (!unlinkResponse.success) {
            setFailReason("Failed to unlink the existing Ganz AiBox, please try again");
            setStep('fail');
            return;
        }

        const ruleUUIDs =
            controlLevel === 'hub' ? [] :
            controlLevel === 'output_relay' ? selectedRelays.map(relay => String(relay.relay_id)) :
            controlLevel === 'channel' ? selectedChannels.map(channel => String(channel.no)) :
            controlLevel === 'rule' ? selectedRules.map(rule => String(rule.uuid)) :
            [];

        const relinkBody = {
            siteUUID,
            pinNumber: pin,
            userID,
            ipAndPort: `${ipAddressState.value}:${portState.value}`,
            zoneConfig: [
                {
                    zoneName,
                    ruleUUIDs,
                    controlLevel
                }
            ]
        };

        const relinkResponse = await addAibox(apiData, relinkBody, dispatch);
        if (relinkResponse.success) {
            setStep('success');
        } else {
            setFailReason("The existing Ganz AiBox was unlinked but relinking with the new details failed - please add it again manually");
            setStep('fail');
        }
    };

    if (step === 'success') {
        return (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                <h3 style={{ textAlign: "center", color: "#858585" }}>Ganz AiBox updated successfully!</h3>
                <Lottie animationData={success} loop={false} style={{ width: 250, height: 250, margin: "0px" }} />
                <Button
                    onClick={() => { refreshDevices?.(); onClose?.(); }}
                    style={{ width: "100%" }}
                    severity='secondary'
                    label='Close'
                    icon="pi pi-times"
                />
            </div>
        );
    }

    if (step === 'fail') {
        return (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                <Lottie animationData={fail} loop={false} style={{ width: 150, height: 150, margin: "0px" }} />
                <h3 style={{ textAlign: "center", color: "#6e6e6eff" }}>{failReason}</h3>
                <Button icon="pi pi-times" onClick={() => setStep('form')} label="Try again" severity='secondary' />
            </div>
        );
    }

    return (
        <form onSubmit={submitHandler} className={styles["container1"]}>
            <h3 style={{ color: "#6e6e6e" }}>Edit Ganz AiBox integration</h3>
            <br />
            <small style={{ color: "#6e6e6e" }}>Please do not include 'https://' or the port in the IP Address field</small>
            <br />

            <input
                placeholder="IP Address"
                value={ipAddressState.value}
                onChange={(e) => dispatchIPAddressState({ type: 'USER_INPUT', val: e.target.value })}
                type="text"
                className={styles.inputField}
                required
            />

            <input
                placeholder="Port"
                value={portState.value}
                onChange={(e) => dispatchPortState({ type: 'USER_INPUT', val: e.target.value })}
                type="text"
                className={styles.inputField}
                required
            />

            <input
                placeholder="Username"
                value={usernameState.value}
                onChange={(e) => dispatchUsernameState({ type: 'USER_INPUT', val: e.target.value })}
                type="text"
                className={styles.inputField}
                required
            />

            <PasswordInput
                placeholder="Password"
                value={passwordState.value}
                onChange={(e) => dispatchPasswordState({ type: 'USER_INPUT', val: e.target.value })}
                className={styles.inputField}
                required
            />

            <div style={{ width: "100%", marginTop: "5px", textAlign: "left" }}>
                <h4 style={{ color: "#6e6e6e" }}>What would you like to control?</h4>
                {optionsLoading && <p style={{ color: "#6e6e6e" }}>Logging in to Ganz AiBox to retrieve channels/rules/relays...</p>}
                {!optionsLoading && optionsError && <p className={styles.errorMessage}>{optionsError}</p>}
                {!optionsLoading && !optionsError && (
                    <>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px" }}>
                            {CONTROL_LEVELS.map(({ value, label }) => (
                                <Button
                                    key={value}
                                    type="button"
                                    severity="secondary"
                                    outlined={controlLevel !== value}
                                    label={label}
                                    style={{ width: "200px" }}
                                    onClick={() => setControlLevel(value)}
                                />
                            ))}
                        </div>

                        {controlLevel === 'channel' && (
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px", marginTop: "15px" }}>
                                {channels.map((channel, index) => (
                                    <SelectableListRow
                                        key={index}
                                        selected={selectedChannels.includes(channel)}
                                        label={`${channel.name} (${channel.no})`}
                                        onClick={() => handleSelectChannel(channel)}
                                    />
                                ))}
                            </div>
                        )}

                        {controlLevel === 'rule' && (
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px", marginTop: "15px" }}>
                                {rules.map((rule, index) => (
                                    <SelectableListRow
                                        key={index}
                                        selected={selectedRules.includes(rule)}
                                        label={rule.name}
                                        onClick={() => handleSelectRule(rule)}
                                    />
                                ))}
                            </div>
                        )}

                        {controlLevel === 'output_relay' && (
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px", marginTop: "15px" }}>
                                {relays.map((relay, index) => (
                                    <SelectableListRow
                                        key={index}
                                        selected={selectedRelays.includes(relay)}
                                        label={relay.name}
                                        onClick={() => handleSelectRelay(relay)}
                                    />
                                ))}
                            </div>
                        )}

                        {controlLevel !== 'hub' && (
                            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginTop: "15px" }}>
                                <label htmlFor="zoneName">Zone name</label>
                                <InputText
                                    id="zoneName"
                                    value={zoneName}
                                    onChange={(e) => setZoneName(e.target.value)}
                                    aria-describedby="zoneName-help"
                                />
                                <small id="zoneName-help">
                                    Set a name for the zone being controlled.
                                </small>
                            </div>
                        )}
                    </>
                )}
            </div>
            <br/>
            <Button
                icon="pi pi-save"
                severity="success"
                type="submit"
                disabled={step === 'saving'}
                label={step === 'saving' ? "Saving..." : "Save"}
            />
        </form>
    );
}

export default AiboxEditForm;
